/**
 * Persistent storage for user-authored / forked tracker songs.
 *
 * Tracks are stored as a JSON array under a single localStorage key. Each
 * track has the same shape as a built-in `Song`, plus an `id` for stable
 * reference across edits and a `forkedFrom` field for provenance when the
 * user duplicates a built-in.
 *
 * The jukebox surfaces user tracks alongside built-ins (built-ins remain
 * read-only). Mutations are written through immediately so a refresh keeps
 * recent edits.
 */

import type { Song } from './Music.js';

export interface UserSong extends Song {
  /** Stable id (slug-ish) used for storage and UI keys. */
  id: string;
  /** Name of the built-in this song was forked from, for provenance. */
  forkedFrom?: string;
}

const KEY = 'shkr.userSongs.v1';

function read(): UserSong[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Trust shape but defensively fill missing arrays.
    return parsed.map((s) => ({
      id: String(s.id ?? Math.random().toString(36).slice(2, 9)),
      name: String(s.name ?? 'Untitled'),
      bpm: Number(s.bpm) || 100,
      lead: Array.isArray(s.lead) && s.lead.length === 16 ? s.lead.map(Number) : new Array(16).fill(0),
      bass: Array.isArray(s.bass) && s.bass.length === 16 ? s.bass.map(Number) : new Array(16).fill(0),
      drums: Array.isArray(s.drums) && s.drums.length === 16
        ? s.drums.map((d: unknown) => (d === 'k' || d === 's' || d === 'h' ? d : 0))
        : new Array(16).fill(0),
      gain: typeof s.gain === 'number' ? s.gain : 0.12,
      ...(s.forkedFrom ? { forkedFrom: String(s.forkedFrom) } : {}),
    }));
  } catch {
    return [];
  }
}

function write(songs: UserSong[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(songs)); } catch { /* ignore quota */ }
}

export function listUserSongs(): UserSong[] {
  return read();
}

/** Create a new empty user song and return it. */
export function createBlank(name = 'New Track'): UserSong {
  const songs = read();
  const song: UserSong = {
    id: Math.random().toString(36).slice(2, 9),
    name,
    bpm: 100,
    lead: new Array(16).fill(0),
    bass: new Array(16).fill(0),
    drums: new Array(16).fill(0),
    gain: 0.12,
  };
  songs.push(song);
  write(songs);
  return song;
}

/** Duplicate a built-in (or any Song) into a new user song. */
export function fork(src: Song): UserSong {
  const songs = read();
  const song: UserSong = {
    id: Math.random().toString(36).slice(2, 9),
    name: `${src.name} (edit)`,
    bpm: src.bpm,
    lead: src.lead.slice(),
    bass: src.bass.slice(),
    drums: src.drums.slice(),
    gain: src.gain ?? 0.12,
    forkedFrom: src.name,
  };
  songs.push(song);
  write(songs);
  return song;
}

/** Persist edits to an existing user song (matched by id). */
export function update(song: UserSong): void {
  const songs = read();
  const idx = songs.findIndex((s) => s.id === song.id);
  if (idx >= 0) songs[idx] = song;
  else songs.push(song);
  write(songs);
}

export function remove(id: string): void {
  write(read().filter((s) => s.id !== id));
}
